/**
 * Per-SKU / org×SKU guarantee configuration (#3135 / #3136).
 *
 * JSONB sections on `guaranteed_guard_configs`. The write face, ops-api,
 * the request-path merge and the console share this module. Unknown fields,
 * out-of-range values and type errors refuse a write; the read path drops
 * illegal fields and treats unknown top-level keys as off.
 *
 * Merge: an org×SKU field that is unset inherits the SKU value; a SKU field
 * that is unset is off; a field set to JSON null turns that field off
 * (`stall.mode: 'off'` does the same for stall). SKU `first_content.stream_ms:
 * null` turns the stream cutoff off even when the legacy column is set.
 * `first_content.stream_ms` on the new table is independent of the legacy
 * `no_response_cutoff_ms` column (that column only feeds `noResponseCutoffMs`).
 * `first_content.nonstream_ms` is armed only once the upstream content tap is
 * actually installed (aggregated SSE/wss). A plain JSON upstream never
 * installs that tap, so a long JSON answer is not cut; the clock starts at
 * tap install, not at fetch start.
 * Trip / recover sections are SKU-only this round (trip scope is global).
 * `first_content.prelude_failover` is Responses-only at the forward seam
 * (org overlay follows the same inherit / null-off rules as stall.mode).
 * `first_content.overload_failover` walks to the next leg on an upstream
 * overloaded-class error on any ingress, without counting the leg as faulty
 * (no broken / silent hit, no breaker failure, no chain / Jev count). It is
 * independent of `prelude_failover` and follows the same inherit / null-off
 * rules.
 * `broken_trip` reuses silent_trip ranges; recover probes are shared with
 * `silent_recover`.
 */

export const GUARD_FIRST_CONTENT_STREAM_MS_MIN = 10_000;
export const GUARD_FIRST_CONTENT_STREAM_MS_MAX = 120_000;
export const GUARD_FIRST_CONTENT_NONSTREAM_MS_MIN = 10_000;
export const GUARD_FIRST_CONTENT_NONSTREAM_MS_MAX = 600_000;

export const GUARD_STALL_CUTOFF_MS_MIN = 15_000;
export const GUARD_STALL_CUTOFF_MS_MAX = 300_000;

export const GUARD_SILENT_TRIP_MIN_HITS_MIN = 1;
export const GUARD_SILENT_TRIP_MIN_HITS_MAX = 100;
export const GUARD_SILENT_TRIP_WINDOW_SECONDS_MIN = 30;
export const GUARD_SILENT_TRIP_WINDOW_SECONDS_MAX = 3_600;
export const GUARD_SILENT_TRIP_MIN_RATIO_MIN = 0;
export const GUARD_SILENT_TRIP_MIN_RATIO_MAX = 1;
export const GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MIN = 60;
export const GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MAX = 86_400;

export const GUARD_SILENT_RECOVER_INTERVAL_SECONDS_MIN = 30;
export const GUARD_SILENT_RECOVER_INTERVAL_SECONDS_MAX = 3_600;
export const GUARD_SILENT_RECOVER_SHOTS_PER_WAVE_MIN = 1;
export const GUARD_SILENT_RECOVER_SHOTS_PER_WAVE_MAX = 8;
export const GUARD_SILENT_RECOVER_CLEAN_WAVES_MIN = 1;
export const GUARD_SILENT_RECOVER_CLEAN_WAVES_MAX = 10;
export const GUARD_SILENT_RECOVER_SHOT_TIMEOUT_SECONDS_MIN = 5;
export const GUARD_SILENT_RECOVER_SHOT_TIMEOUT_SECONDS_MAX = 120;

export const GUARD_MODES = ['off', 'shadow', 'act'] as const;
export type GuardMode = (typeof GUARD_MODES)[number];

export const GUARD_PROBE_KINDS = ['codex-cli', 'claude-code-cli', 'chat-stream'] as const;
export type GuardProbeKind = (typeof GUARD_PROBE_KINDS)[number];

export const GUARD_SECTION_KEYS = ['first_content', 'stall', 'silent_trip', 'silent_recover', 'broken_trip'] as const;
export type GuardSectionKey = (typeof GUARD_SECTION_KEYS)[number];

export const GUARD_SKU_ONLY_SECTIONS: readonly GuardSectionKey[] = ['silent_trip', 'silent_recover', 'broken_trip'];

/** Stored / patched first_content. JSON null on a field is explicit off (org overlay). */
export interface GuardFirstContentSection {
  readonly stream_ms?: number | null;
  readonly nonstream_ms?: number | null;
  readonly prelude_failover?: GuardMode | null;
  readonly overload_failover?: GuardMode | null;
}

export interface GuardStallSection {
  readonly mode?: GuardMode | null;
  readonly cutoff_ms?: number | null;
  readonly retry_after_reasoning?: boolean | null;
}

export interface GuardSilentTripSection {
  readonly mode?: GuardMode | null;
  readonly min_hits?: number | null;
  readonly window_seconds?: number | null;
  readonly min_ratio?: number | null;
  readonly max_hold_seconds?: number | null;
}

export interface GuardSilentRecoverSection {
  readonly probe_kind?: GuardProbeKind | null;
  readonly interval_seconds?: number | null;
  readonly shots_per_wave?: number | null;
  readonly clean_waves?: number | null;
  readonly shot_timeout_seconds?: number | null;
}

/** Same shape and ranges as {@link GuardSilentTripSection}. SKU-only. */
export type GuardBrokenTripSection = GuardSilentTripSection;

/** Stored JSONB. Missing section = off (SKU) / inherit (org overlay). */
export interface StoredGuardConfig {
  readonly first_content?: GuardFirstContentSection;
  readonly stall?: GuardStallSection;
  readonly silent_trip?: GuardSilentTripSection;
  readonly silent_recover?: GuardSilentRecoverSection;
  readonly broken_trip?: GuardBrokenTripSection;
}

/**
 * Merged effective config from the new table only. Off sections are omitted
 * so a fully-off result is `null` and never appears as a key on the routing
 * result or Tair blob. The legacy cutoff column is not folded in.
 */
export interface ResolvedGuardConfig {
  readonly first_content?: {
    readonly stream_ms?: number;
    readonly nonstream_ms?: number;
    readonly prelude_failover?: 'shadow' | 'act';
    readonly overload_failover?: 'shadow' | 'act';
  };
  readonly stall?: {
    readonly mode: 'shadow' | 'act';
    readonly cutoff_ms: number;
    readonly retry_after_reasoning: boolean;
  };
  readonly silent_trip?: {
    readonly mode: 'shadow' | 'act';
    readonly min_hits: number;
    readonly window_seconds: number;
    readonly min_ratio: number;
    readonly max_hold_seconds: number;
  };
  readonly silent_recover?: {
    readonly probe_kind: GuardProbeKind;
    readonly interval_seconds: number;
    readonly shots_per_wave: number;
    readonly clean_waves: number;
    readonly shot_timeout_seconds: number;
  };
  readonly broken_trip?: {
    readonly mode: 'shadow' | 'act';
    readonly min_hits: number;
    readonly window_seconds: number;
    readonly min_ratio: number;
    readonly max_hold_seconds: number;
  };
}

export type GuardParseOk<T> = { readonly ok: true; readonly value: T };
export type GuardParseErr = { readonly ok: false; readonly message: string };
export type GuardParseResult<T> = GuardParseOk<T> | GuardParseErr;

export type GuardUnsetPath = readonly [GuardSectionKey, string];

export interface GuardConfigWritePatch {
  readonly removed: readonly GuardSectionKey[];
  readonly patches: StoredGuardConfig;
  /** Subkeys to delete (`jsonb #-`) so an org field returns to inherit. */
  readonly unsetPaths: readonly GuardUnsetPath[];
}

const FIRST_CONTENT_FIELDS = new Set(['stream_ms', 'nonstream_ms', 'prelude_failover', 'overload_failover']);
const STALL_FIELDS = new Set(['mode', 'cutoff_ms', 'retry_after_reasoning']);
const SILENT_TRIP_FIELDS = new Set(['mode', 'min_hits', 'window_seconds', 'min_ratio', 'max_hold_seconds']);
const SILENT_RECOVER_FIELDS = new Set([
  'probe_kind', 'interval_seconds', 'shots_per_wave', 'clean_waves', 'shot_timeout_seconds',
]);
const BROKEN_TRIP_FIELDS = SILENT_TRIP_FIELDS;
const SECTION_KEY_SET = new Set<string>(GUARD_SECTION_KEYS);
const UNSET_KEY = 'unset';
const SECTION_FIELDS: Record<GuardSectionKey, ReadonlySet<string>> = {
  first_content: FIRST_CONTENT_FIELDS,
  stall: STALL_FIELDS,
  silent_trip: SILENT_TRIP_FIELDS,
  silent_recover: SILENT_RECOVER_FIELDS,
  broken_trip: BROKEN_TRIP_FIELDS,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function unknownKeys(record: Record<string, unknown>, allowed: ReadonlySet<string>): string[] {
  return Object.keys(record).filter((key) => !allowed.has(key)).sort();
}

function isGuardMode(value: unknown): value is GuardMode {
  return value === 'off' || value === 'shadow' || value === 'act';
}

function isGuardProbeKind(value: unknown): value is GuardProbeKind {
  for (const kind of GUARD_PROBE_KINDS) {
    if (kind === value) return true;
  }
  return false;
}

function isIntInRange(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
}

function isRatio(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
    && value >= GUARD_SILENT_TRIP_MIN_RATIO_MIN && value <= GUARD_SILENT_TRIP_MIN_RATIO_MAX;
}

function rangeMessage(name: string, min: number, max: number): string {
  return `${name} must be an integer from ${String(min)} to ${String(max)}`;
}

function fail(message: string): GuardParseErr {
  return { ok: false, message };
}

function parseSectionUnset(
  raw: unknown,
  section: GuardSectionKey,
): GuardParseResult<readonly string[]> {
  if (!isPlainObject(raw) || !(UNSET_KEY in raw)) return { ok: true, value: [] };
  const value = raw[UNSET_KEY];
  if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) {
    return fail(`${section}.unset must be an array of field names`);
  }
  const allowed = SECTION_FIELDS[section];
  const extra = value.filter((item) => !allowed.has(item)).sort();
  if (extra.length > 0) return fail(`${section}.unset has unknown field(s): ${extra.join(', ')}`);
  return { ok: true, value: [...new Set(value)] };
}

function sectionBodyWithoutUnset(raw: unknown): unknown {
  if (!isPlainObject(raw) || !(UNSET_KEY in raw)) return raw;
  const rest: Record<string, unknown> = {};
  for (const key of Object.keys(raw)) {
    if (key !== UNSET_KEY) rest[key] = raw[key];
  }
  return rest;
}

function optionalInt(
  value: unknown,
  name: string,
  min: number,
  max: number,
  allowNull: boolean,
): GuardParseResult<number | null | undefined> {
  if (value === undefined) return { ok: true, value: undefined };
  if (value === null) return allowNull ? { ok: true, value: null } : fail(`${name} must be ${rangeMessage(name, min, max)}`);
  if (!isIntInRange(value, min, max)) return fail(rangeMessage(name, min, max));
  return { ok: true, value };
}

function parseFirstContentWrite(
  value: unknown,
  allowNullFields: boolean,
): GuardParseResult<GuardFirstContentSection | undefined> {
  if (value === null) return { ok: true, value: undefined };
  if (!isPlainObject(value)) return fail('first_content must be an object or null');
  const extra = unknownKeys(value, FIRST_CONTENT_FIELDS);
  if (extra.length > 0) return fail(`first_content has unknown field(s): ${extra.join(', ')}`);
  const out: {
    stream_ms?: number | null;
    nonstream_ms?: number | null;
    prelude_failover?: GuardMode | null;
    overload_failover?: GuardMode | null;
  } = {};
  if ('stream_ms' in value) {
    const parsed = optionalInt(
      value.stream_ms, 'first_content.stream_ms',
      GUARD_FIRST_CONTENT_STREAM_MS_MIN, GUARD_FIRST_CONTENT_STREAM_MS_MAX, allowNullFields,
    );
    if (!parsed.ok) return parsed;
    if (parsed.value !== undefined) out.stream_ms = parsed.value;
  }
  if ('nonstream_ms' in value) {
    const parsed = optionalInt(
      value.nonstream_ms, 'first_content.nonstream_ms',
      GUARD_FIRST_CONTENT_NONSTREAM_MS_MIN, GUARD_FIRST_CONTENT_NONSTREAM_MS_MAX, allowNullFields,
    );
    if (!parsed.ok) return parsed;
    if (parsed.value !== undefined) out.nonstream_ms = parsed.value;
  }
  if ('prelude_failover' in value) {
    if (value.prelude_failover === null) {
      if (!allowNullFields) return fail("first_content.prelude_failover must be 'off', 'shadow' or 'act'");
      out.prelude_failover = null;
    } else if (!isGuardMode(value.prelude_failover)) {
      return fail("first_content.prelude_failover must be 'off', 'shadow' or 'act'");
    } else {
      out.prelude_failover = value.prelude_failover;
    }
  }
  if ('overload_failover' in value) {
    if (value.overload_failover === null) {
      if (!allowNullFields) return fail("first_content.overload_failover must be 'off', 'shadow' or 'act'");
      out.overload_failover = null;
    } else if (!isGuardMode(value.overload_failover)) {
      return fail("first_content.overload_failover must be 'off', 'shadow' or 'act'");
    } else {
      out.overload_failover = value.overload_failover;
    }
  }
  return { ok: true, value: out };
}

function parseStallWrite(
  value: unknown,
  requireComplete: boolean,
): GuardParseResult<GuardStallSection | undefined> {
  if (value === null) return { ok: true, value: undefined };
  if (!isPlainObject(value)) return fail('stall must be an object or null');
  const extra = unknownKeys(value, STALL_FIELDS);
  if (extra.length > 0) return fail(`stall has unknown field(s): ${extra.join(', ')}`);
  if (requireComplete) {
    if (!isGuardMode(value.mode)) return fail("stall.mode must be 'off', 'shadow' or 'act'");
    if (!isIntInRange(value.cutoff_ms, GUARD_STALL_CUTOFF_MS_MIN, GUARD_STALL_CUTOFF_MS_MAX)) {
      return fail(rangeMessage('stall.cutoff_ms', GUARD_STALL_CUTOFF_MS_MIN, GUARD_STALL_CUTOFF_MS_MAX));
    }
    if (typeof value.retry_after_reasoning !== 'boolean') {
      return fail('stall.retry_after_reasoning must be a boolean');
    }
    return {
      ok: true,
      value: { mode: value.mode, cutoff_ms: value.cutoff_ms, retry_after_reasoning: value.retry_after_reasoning },
    };
  }
  const out: { mode?: GuardMode | null; cutoff_ms?: number | null; retry_after_reasoning?: boolean | null } = {};
  if ('mode' in value) {
    if (value.mode === null) out.mode = null;
    else if (!isGuardMode(value.mode)) return fail("stall.mode must be 'off', 'shadow' or 'act'");
    else out.mode = value.mode;
  }
  if ('cutoff_ms' in value) {
    const parsed = optionalInt(value.cutoff_ms, 'stall.cutoff_ms', GUARD_STALL_CUTOFF_MS_MIN, GUARD_STALL_CUTOFF_MS_MAX, true);
    if (!parsed.ok) return parsed;
    if (parsed.value !== undefined) out.cutoff_ms = parsed.value;
  }
  if ('retry_after_reasoning' in value) {
    if (value.retry_after_reasoning === null) out.retry_after_reasoning = null;
    else if (typeof value.retry_after_reasoning !== 'boolean') return fail('stall.retry_after_reasoning must be a boolean');
    else out.retry_after_reasoning = value.retry_after_reasoning;
  }
  return { ok: true, value: out };
}

function parseSilentTripWrite(
  value: unknown,
  requireComplete: boolean,
): GuardParseResult<GuardSilentTripSection | undefined> {
  if (value === null) return { ok: true, value: undefined };
  if (!isPlainObject(value)) return fail('silent_trip must be an object or null');
  const extra = unknownKeys(value, SILENT_TRIP_FIELDS);
  if (extra.length > 0) return fail(`silent_trip has unknown field(s): ${extra.join(', ')}`);
  if (!requireComplete) return fail('silent_trip cannot be set on an org×SKU row; trip, recover and broken_trip are SKU-only');
  if (!isGuardMode(value.mode)) return fail("silent_trip.mode must be 'off', 'shadow' or 'act'");
  if (!isIntInRange(value.min_hits, GUARD_SILENT_TRIP_MIN_HITS_MIN, GUARD_SILENT_TRIP_MIN_HITS_MAX)) {
    return fail(rangeMessage('silent_trip.min_hits', GUARD_SILENT_TRIP_MIN_HITS_MIN, GUARD_SILENT_TRIP_MIN_HITS_MAX));
  }
  if (!isIntInRange(value.window_seconds, GUARD_SILENT_TRIP_WINDOW_SECONDS_MIN, GUARD_SILENT_TRIP_WINDOW_SECONDS_MAX)) {
    return fail(rangeMessage('silent_trip.window_seconds', GUARD_SILENT_TRIP_WINDOW_SECONDS_MIN, GUARD_SILENT_TRIP_WINDOW_SECONDS_MAX));
  }
  if (!isRatio(value.min_ratio)) {
    return fail(`silent_trip.min_ratio must be a number from ${String(GUARD_SILENT_TRIP_MIN_RATIO_MIN)} to ${String(GUARD_SILENT_TRIP_MIN_RATIO_MAX)}`);
  }
  if (!isIntInRange(value.max_hold_seconds, GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MIN, GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MAX)) {
    return fail(rangeMessage('silent_trip.max_hold_seconds', GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MIN, GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MAX));
  }
  return {
    ok: true,
    value: {
      mode: value.mode,
      min_hits: value.min_hits,
      window_seconds: value.window_seconds,
      min_ratio: value.min_ratio,
      max_hold_seconds: value.max_hold_seconds,
    },
  };
}

function parseSilentRecoverWrite(
  value: unknown,
  requireComplete: boolean,
): GuardParseResult<GuardSilentRecoverSection | undefined> {
  if (value === null) return { ok: true, value: undefined };
  if (!isPlainObject(value)) return fail('silent_recover must be an object or null');
  const extra = unknownKeys(value, SILENT_RECOVER_FIELDS);
  if (extra.length > 0) return fail(`silent_recover has unknown field(s): ${extra.join(', ')}`);
  if (!requireComplete) return fail('silent_recover cannot be set on an org×SKU row; trip, recover and broken_trip are SKU-only');
  if (!isGuardProbeKind(value.probe_kind)) {
    return fail("silent_recover.probe_kind must be 'codex-cli', 'claude-code-cli', or 'chat-stream'");
  }
  if (!isIntInRange(value.interval_seconds, GUARD_SILENT_RECOVER_INTERVAL_SECONDS_MIN, GUARD_SILENT_RECOVER_INTERVAL_SECONDS_MAX)) {
    return fail(rangeMessage('silent_recover.interval_seconds', GUARD_SILENT_RECOVER_INTERVAL_SECONDS_MIN, GUARD_SILENT_RECOVER_INTERVAL_SECONDS_MAX));
  }
  if (!isIntInRange(value.shots_per_wave, GUARD_SILENT_RECOVER_SHOTS_PER_WAVE_MIN, GUARD_SILENT_RECOVER_SHOTS_PER_WAVE_MAX)) {
    return fail(rangeMessage('silent_recover.shots_per_wave', GUARD_SILENT_RECOVER_SHOTS_PER_WAVE_MIN, GUARD_SILENT_RECOVER_SHOTS_PER_WAVE_MAX));
  }
  if (!isIntInRange(value.clean_waves, GUARD_SILENT_RECOVER_CLEAN_WAVES_MIN, GUARD_SILENT_RECOVER_CLEAN_WAVES_MAX)) {
    return fail(rangeMessage('silent_recover.clean_waves', GUARD_SILENT_RECOVER_CLEAN_WAVES_MIN, GUARD_SILENT_RECOVER_CLEAN_WAVES_MAX));
  }
  if (!isIntInRange(
    value.shot_timeout_seconds,
    GUARD_SILENT_RECOVER_SHOT_TIMEOUT_SECONDS_MIN,
    GUARD_SILENT_RECOVER_SHOT_TIMEOUT_SECONDS_MAX,
  )) {
    return fail(rangeMessage(
      'silent_recover.shot_timeout_seconds',
      GUARD_SILENT_RECOVER_SHOT_TIMEOUT_SECONDS_MIN,
      GUARD_SILENT_RECOVER_SHOT_TIMEOUT_SECONDS_MAX,
    ));
  }
  return {
    ok: true,
    value: {
      probe_kind: value.probe_kind,
      interval_seconds: value.interval_seconds,
      shots_per_wave: value.shots_per_wave,
      clean_waves: value.clean_waves,
      shot_timeout_seconds: value.shot_timeout_seconds,
    },
  };
}

function parseBrokenTripWrite(
  value: unknown,
  requireComplete: boolean,
): GuardParseResult<GuardBrokenTripSection | undefined> {
  if (value === null) return { ok: true, value: undefined };
  if (!isPlainObject(value)) return fail('broken_trip must be an object or null');
  const extra = unknownKeys(value, BROKEN_TRIP_FIELDS);
  if (extra.length > 0) return fail(`broken_trip has unknown field(s): ${extra.join(', ')}`);
  if (!requireComplete) return fail('broken_trip cannot be set on an org×SKU row; trip, recover and broken_trip are SKU-only');
  if (!isGuardMode(value.mode)) return fail("broken_trip.mode must be 'off', 'shadow' or 'act'");
  if (!isIntInRange(value.min_hits, GUARD_SILENT_TRIP_MIN_HITS_MIN, GUARD_SILENT_TRIP_MIN_HITS_MAX)) {
    return fail(rangeMessage('broken_trip.min_hits', GUARD_SILENT_TRIP_MIN_HITS_MIN, GUARD_SILENT_TRIP_MIN_HITS_MAX));
  }
  if (!isIntInRange(value.window_seconds, GUARD_SILENT_TRIP_WINDOW_SECONDS_MIN, GUARD_SILENT_TRIP_WINDOW_SECONDS_MAX)) {
    return fail(rangeMessage('broken_trip.window_seconds', GUARD_SILENT_TRIP_WINDOW_SECONDS_MIN, GUARD_SILENT_TRIP_WINDOW_SECONDS_MAX));
  }
  if (!isRatio(value.min_ratio)) {
    return fail(`broken_trip.min_ratio must be a number from ${String(GUARD_SILENT_TRIP_MIN_RATIO_MIN)} to ${String(GUARD_SILENT_TRIP_MIN_RATIO_MAX)}`);
  }
  if (!isIntInRange(value.max_hold_seconds, GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MIN, GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MAX)) {
    return fail(rangeMessage('broken_trip.max_hold_seconds', GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MIN, GUARD_SILENT_TRIP_MAX_HOLD_SECONDS_MAX));
  }
  return {
    ok: true,
    value: {
      mode: value.mode,
      min_hits: value.min_hits,
      window_seconds: value.window_seconds,
      min_ratio: value.min_ratio,
      max_hold_seconds: value.max_hold_seconds,
    },
  };
}

/**
 * Strict write parser. SKU sections that are present must be complete.
 * Org×SKU sections are per-field: omitted inherits, JSON null turns off.
 * Present keys, including a null section, are listed in `removed` / `patches`
 * so the write face can merge in one SQL statement.
 */
export function parseGuardConfigWrite(
  input: unknown,
  options: { readonly scope: 'sku' | 'org_sku'; readonly partial: boolean },
): GuardParseResult<GuardConfigWritePatch> {
  if (!isPlainObject(input)) return fail('config must be an object');
  const extra = unknownKeys(input, SECTION_KEY_SET);
  if (extra.length > 0) return fail(`unknown field(s): ${extra.join(', ')}`);
  if (options.scope === 'org_sku') {
    const refused = GUARD_SKU_ONLY_SECTIONS.filter((key) => key in input);
    if (refused.length > 0) {
      return fail(`${refused.join(' and ')} cannot be set on an org×SKU row; trip, recover and broken_trip are SKU-only`);
    }
  }
  const org = options.scope === 'org_sku';
  const removed: GuardSectionKey[] = [];
  const patches: {
    first_content?: GuardFirstContentSection;
    stall?: GuardStallSection;
    silent_trip?: GuardSilentTripSection;
    silent_recover?: GuardSilentRecoverSection;
    broken_trip?: GuardBrokenTripSection;
  } = {};
  const unsetPaths: GuardUnsetPath[] = [];

  const take = <K extends GuardSectionKey>(
    key: K,
    parsed: GuardParseResult<StoredGuardConfig[K] | undefined>,
    present: boolean,
  ): GuardParseErr | null => {
    if (!parsed.ok) return parsed;
    if (!present) return null;
    if (parsed.value === undefined) removed.push(key);
    else if (Object.keys(parsed.value).length > 0) Object.assign(patches, { [key]: parsed.value });
    return null;
  };

  const takeUnset = (key: GuardSectionKey, raw: unknown): GuardParseErr | null => {
    const parsed = parseSectionUnset(raw, key);
    if (!parsed.ok) return parsed;
    const body = sectionBodyWithoutUnset(raw);
    if (isPlainObject(body)) {
      const both = parsed.value.filter((field) => field in body);
      if (both.length > 0) {
        return fail(`cannot both set and unset ${key}.${both.sort().join(`, ${key}.`)}`);
      }
    }
    for (const field of parsed.value) unsetPaths.push([key, field]);
    return null;
  };

  const firstPresent = 'first_content' in input || !options.partial;
  const firstUnsetErr = 'first_content' in input ? takeUnset('first_content', input.first_content) : null;
  if (firstUnsetErr) return firstUnsetErr;
  const first = 'first_content' in input
    ? parseFirstContentWrite(sectionBodyWithoutUnset(input.first_content), true)
    : { ok: true as const, value: undefined };
  const firstErr = take('first_content', first, firstPresent && 'first_content' in input);
  if (firstErr) return firstErr;

  const stallPresent = 'stall' in input || !options.partial;
  const stallUnsetErr = 'stall' in input ? takeUnset('stall', input.stall) : null;
  if (stallUnsetErr) return stallUnsetErr;
  const stall = 'stall' in input
    ? parseStallWrite(sectionBodyWithoutUnset(input.stall), !org)
    : { ok: true as const, value: undefined };
  const stallErr = take('stall', stall, stallPresent && 'stall' in input);
  if (stallErr) return stallErr;

  const tripPresent = 'silent_trip' in input || !options.partial;
  const tripUnsetErr = 'silent_trip' in input ? takeUnset('silent_trip', input.silent_trip) : null;
  if (tripUnsetErr) return tripUnsetErr;
  const trip = 'silent_trip' in input
    ? parseSilentTripWrite(sectionBodyWithoutUnset(input.silent_trip), !org)
    : { ok: true as const, value: undefined };
  const tripErr = take('silent_trip', trip, tripPresent && 'silent_trip' in input);
  if (tripErr) return tripErr;

  const recoverPresent = 'silent_recover' in input || !options.partial;
  const recoverUnsetErr = 'silent_recover' in input ? takeUnset('silent_recover', input.silent_recover) : null;
  if (recoverUnsetErr) return recoverUnsetErr;
  const recover = 'silent_recover' in input
    ? parseSilentRecoverWrite(sectionBodyWithoutUnset(input.silent_recover), !org)
    : { ok: true as const, value: undefined };
  const recoverErr = take('silent_recover', recover, recoverPresent && 'silent_recover' in input);
  if (recoverErr) return recoverErr;

  const brokenPresent = 'broken_trip' in input || !options.partial;
  const brokenUnsetErr = 'broken_trip' in input ? takeUnset('broken_trip', input.broken_trip) : null;
  if (brokenUnsetErr) return brokenUnsetErr;
  const broken = 'broken_trip' in input
    ? parseBrokenTripWrite(sectionBodyWithoutUnset(input.broken_trip), !org)
    : { ok: true as const, value: undefined };
  const brokenErr = take('broken_trip', broken, brokenPresent && 'broken_trip' in input);
  if (brokenErr) return brokenErr;

  return { ok: true, value: { removed, patches, unsetPaths } };
}

function readIntField(value: unknown, min: number, max: number): number | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return isIntInRange(value, min, max) ? value : undefined;
}

function readFirstContent(value: unknown): GuardFirstContentSection | undefined {
  if (!isPlainObject(value)) return undefined;
  const out: {
    stream_ms?: number | null;
    nonstream_ms?: number | null;
    prelude_failover?: GuardMode | null;
    overload_failover?: GuardMode | null;
  } = {};
  if ('stream_ms' in value) {
    const parsed = readIntField(value.stream_ms, GUARD_FIRST_CONTENT_STREAM_MS_MIN, GUARD_FIRST_CONTENT_STREAM_MS_MAX);
    if (value.stream_ms === null) out.stream_ms = null;
    else if (parsed !== undefined && parsed !== null) out.stream_ms = parsed;
  }
  if ('nonstream_ms' in value) {
    const parsed = readIntField(value.nonstream_ms, GUARD_FIRST_CONTENT_NONSTREAM_MS_MIN, GUARD_FIRST_CONTENT_NONSTREAM_MS_MAX);
    if (value.nonstream_ms === null) out.nonstream_ms = null;
    else if (parsed !== undefined && parsed !== null) out.nonstream_ms = parsed;
  }
  if ('prelude_failover' in value) {
    if (value.prelude_failover === null) out.prelude_failover = null;
    else if (isGuardMode(value.prelude_failover)) out.prelude_failover = value.prelude_failover;
  }
  if ('overload_failover' in value) {
    if (value.overload_failover === null) out.overload_failover = null;
    else if (isGuardMode(value.overload_failover)) out.overload_failover = value.overload_failover;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

function readStall(value: unknown): GuardStallSection | undefined {
  if (!isPlainObject(value)) return undefined;
  const out: { mode?: GuardMode | null; cutoff_ms?: number | null; retry_after_reasoning?: boolean | null } = {};
  if ('mode' in value) {
    if (value.mode === null) out.mode = null;
    else if (isGuardMode(value.mode)) out.mode = value.mode;
  }
  if ('cutoff_ms' in value) {
    if (value.cutoff_ms === null) out.cutoff_ms = null;
    else if (isIntInRange(value.cutoff_ms, GUARD_STALL_CUTOFF_MS_MIN, GUARD_STALL_CUTOFF_MS_MAX)) out.cutoff_ms = value.cutoff_ms;
  }
  if ('retry_after_reasoning' in value) {
    if (value.retry_after_reasoning === null) out.retry_after_reasoning = null;
    else if (typeof value.retry_after_reasoning === 'boolean') out.retry_after_reasoning = value.retry_after_reasoning;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

function readSilentTrip(value: unknown): GuardSilentTripSection | undefined {
  const parsed = parseSilentTripWrite(value, true);
  return parsed.ok ? parsed.value : undefined;
}

function readSilentRecover(value: unknown): GuardSilentRecoverSection | undefined {
  const parsed = parseSilentRecoverWrite(value, true);
  return parsed.ok ? parsed.value : undefined;
}

function readBrokenTrip(value: unknown): GuardBrokenTripSection | undefined {
  const parsed = parseBrokenTripWrite(value, true);
  return parsed.ok ? parsed.value : undefined;
}

/**
 * Read parser. Unknown top-level keys → empty. Illegal fields inside a
 * section are dropped (inherit); JSON null is kept as explicit off.
 */
export function parseGuardConfigRead(input: unknown): StoredGuardConfig {
  if (input === null || input === undefined) return {};
  if (!isPlainObject(input)) return {};
  if (unknownKeys(input, SECTION_KEY_SET).length > 0) return {};
  const out: {
    first_content?: GuardFirstContentSection;
    stall?: GuardStallSection;
    silent_trip?: GuardSilentTripSection;
    silent_recover?: GuardSilentRecoverSection;
    broken_trip?: GuardBrokenTripSection;
  } = {};
  if ('first_content' in input) {
    const section = readFirstContent(input.first_content);
    if (section) out.first_content = section;
  }
  if ('stall' in input) {
    const section = readStall(input.stall);
    if (section) out.stall = section;
  }
  if ('silent_trip' in input) {
    const section = readSilentTrip(input.silent_trip);
    if (section) out.silent_trip = section;
  }
  if ('silent_recover' in input) {
    const section = readSilentRecover(input.silent_recover);
    if (section) out.silent_recover = section;
  }
  if ('broken_trip' in input) {
    const section = readBrokenTrip(input.broken_trip);
    if (section) out.broken_trip = section;
  }
  return out;
}

function hasField<K extends PropertyKey>(section: object | undefined, field: K): boolean {
  return section !== undefined && field in section;
}

function overlay<T>(
  orgHas: boolean,
  orgValue: T | null | undefined,
  skuHas: boolean,
  skuValue: T | null | undefined,
): T | undefined {
  if (orgHas) return orgValue === null || orgValue === undefined ? undefined : orgValue;
  if (skuHas) return skuValue === null || skuValue === undefined ? undefined : skuValue;
  return undefined;
}

function armedMode(mode: GuardMode | undefined): 'shadow' | 'act' | undefined {
  return mode === 'shadow' || mode === 'act' ? mode : undefined;
}

/**
 * Pure merge of new-table rows. Org field unset inherits SKU; SKU unset is
 * off; org JSON null is off. Does not fold the legacy cutoff column.
 */
export function resolveGuardConfig(params: {
  readonly sku?: unknown;
  readonly orgSku?: unknown;
}): ResolvedGuardConfig | null {
  const sku = parseGuardConfigRead(params.sku);
  const org = parseGuardConfigRead(params.orgSku);
  const streamMs = overlay(
    hasField(org.first_content, 'stream_ms'), org.first_content?.stream_ms,
    hasField(sku.first_content, 'stream_ms'), sku.first_content?.stream_ms,
  );
  const nonstreamMs = overlay(
    hasField(org.first_content, 'nonstream_ms'), org.first_content?.nonstream_ms,
    hasField(sku.first_content, 'nonstream_ms'), sku.first_content?.nonstream_ms,
  );
  const preludeFailover = armedMode(overlay(
    hasField(org.first_content, 'prelude_failover'), org.first_content?.prelude_failover,
    hasField(sku.first_content, 'prelude_failover'), sku.first_content?.prelude_failover,
  ));
  const overloadFailover = armedMode(overlay(
    hasField(org.first_content, 'overload_failover'), org.first_content?.overload_failover,
    hasField(sku.first_content, 'overload_failover'), sku.first_content?.overload_failover,
  ));
  const firstContent = streamMs !== undefined || nonstreamMs !== undefined
    || preludeFailover !== undefined || overloadFailover !== undefined
    ? {
      ...(streamMs !== undefined ? { stream_ms: streamMs } : {}),
      ...(nonstreamMs !== undefined ? { nonstream_ms: nonstreamMs } : {}),
      ...(preludeFailover !== undefined ? { prelude_failover: preludeFailover } : {}),
      ...(overloadFailover !== undefined ? { overload_failover: overloadFailover } : {}),
    }
    : undefined;

  const stallMode = armedMode(overlay(
    hasField(org.stall, 'mode'), org.stall?.mode,
    hasField(sku.stall, 'mode'), sku.stall?.mode,
  ));
  const stallCutoff = overlay(
    hasField(org.stall, 'cutoff_ms'), org.stall?.cutoff_ms,
    hasField(sku.stall, 'cutoff_ms'), sku.stall?.cutoff_ms,
  );
  const stallRetry = overlay(
    hasField(org.stall, 'retry_after_reasoning'), org.stall?.retry_after_reasoning,
    hasField(sku.stall, 'retry_after_reasoning'), sku.stall?.retry_after_reasoning,
  );
  const stall = stallMode !== undefined && stallCutoff !== undefined
    ? { mode: stallMode, cutoff_ms: stallCutoff, retry_after_reasoning: stallRetry === true }
    : undefined;

  const tripMode = armedMode(overlay(false, undefined, hasField(sku.silent_trip, 'mode'), sku.silent_trip?.mode));
  const tripHits = overlay(false, undefined, hasField(sku.silent_trip, 'min_hits'), sku.silent_trip?.min_hits);
  const tripWindow = overlay(false, undefined, hasField(sku.silent_trip, 'window_seconds'), sku.silent_trip?.window_seconds);
  const tripRatio = overlay(false, undefined, hasField(sku.silent_trip, 'min_ratio'), sku.silent_trip?.min_ratio);
  const tripHold = overlay(false, undefined, hasField(sku.silent_trip, 'max_hold_seconds'), sku.silent_trip?.max_hold_seconds);
  const silentTrip = tripMode !== undefined
    && tripHits !== undefined && tripWindow !== undefined && tripRatio !== undefined && tripHold !== undefined
    ? { mode: tripMode, min_hits: tripHits, window_seconds: tripWindow, min_ratio: tripRatio, max_hold_seconds: tripHold }
    : undefined;

  const probeKind = overlay(false, undefined, hasField(sku.silent_recover, 'probe_kind'), sku.silent_recover?.probe_kind);
  const recoverInterval = overlay(false, undefined, hasField(sku.silent_recover, 'interval_seconds'), sku.silent_recover?.interval_seconds);
  const recoverShots = overlay(false, undefined, hasField(sku.silent_recover, 'shots_per_wave'), sku.silent_recover?.shots_per_wave);
  const recoverWaves = overlay(false, undefined, hasField(sku.silent_recover, 'clean_waves'), sku.silent_recover?.clean_waves);
  const recoverTimeout = overlay(false, undefined, hasField(sku.silent_recover, 'shot_timeout_seconds'), sku.silent_recover?.shot_timeout_seconds);
  const silentRecover = probeKind !== undefined
    && recoverInterval !== undefined && recoverShots !== undefined
    && recoverWaves !== undefined && recoverTimeout !== undefined
    ? {
      probe_kind: probeKind,
      interval_seconds: recoverInterval,
      shots_per_wave: recoverShots,
      clean_waves: recoverWaves,
      shot_timeout_seconds: recoverTimeout,
    }
    : undefined;

  const brokenMode = armedMode(overlay(false, undefined, hasField(sku.broken_trip, 'mode'), sku.broken_trip?.mode));
  const brokenHits = overlay(false, undefined, hasField(sku.broken_trip, 'min_hits'), sku.broken_trip?.min_hits);
  const brokenWindow = overlay(false, undefined, hasField(sku.broken_trip, 'window_seconds'), sku.broken_trip?.window_seconds);
  const brokenRatio = overlay(false, undefined, hasField(sku.broken_trip, 'min_ratio'), sku.broken_trip?.min_ratio);
  const brokenHold = overlay(false, undefined, hasField(sku.broken_trip, 'max_hold_seconds'), sku.broken_trip?.max_hold_seconds);
  const brokenTrip = brokenMode !== undefined
    && brokenHits !== undefined && brokenWindow !== undefined && brokenRatio !== undefined && brokenHold !== undefined
    ? { mode: brokenMode, min_hits: brokenHits, window_seconds: brokenWindow, min_ratio: brokenRatio, max_hold_seconds: brokenHold }
    : undefined;

  if (
    firstContent === undefined && stall === undefined && silentTrip === undefined
    && silentRecover === undefined && brokenTrip === undefined
  ) {
    return null;
  }
  return {
    ...(firstContent !== undefined ? { first_content: firstContent } : {}),
    ...(stall !== undefined ? { stall } : {}),
    ...(silentTrip !== undefined ? { silent_trip: silentTrip } : {}),
    ...(silentRecover !== undefined ? { silent_recover: silentRecover } : {}),
    ...(brokenTrip !== undefined ? { broken_trip: brokenTrip } : {}),
  };
}

/**
 * Effective stream first-content cutoff for forwarding: new-table merge
 * (including org JSON null = off) wins; otherwise the legacy column.
 */
export function resolveNoResponseCutoffMs(params: {
  readonly sku?: unknown;
  readonly orgSku?: unknown;
  readonly legacyStreamCutoffMs?: number | null;
}): number | null {
  const sku = parseGuardConfigRead(params.sku);
  const org = parseGuardConfigRead(params.orgSku);
  if (org.first_content !== undefined && 'stream_ms' in org.first_content) {
    const value = org.first_content.stream_ms;
    return value === null || value === undefined ? null : value;
  }
  if (sku.first_content !== undefined && 'stream_ms' in sku.first_content) {
    const value = sku.first_content.stream_ms;
    return value === null || value === undefined ? null : value;
  }
  const legacy = params.legacyStreamCutoffMs;
  return legacy !== null && legacy !== undefined
    && isIntInRange(legacy, GUARD_FIRST_CONTENT_STREAM_MS_MIN, GUARD_FIRST_CONTENT_STREAM_MS_MAX)
    ? legacy
    : null;
}
